/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { LayoutContext } from 'contexts';
import { Page } from 'components';
import { useBreakpoint } from 'hooks';
import TourHeaderMenu from './TourHeaderMenu';
import TourSideNav from './TourSideNav';
import TourMapView from './TourMapView';
import TourListingModal from './TourListingModal';
import SendRequests from './TourSideNav/SendRequests';
import TourEditContext from './TourEditContext';
import { useFloatingPanelAnchors } from './hooks';
import EnhancedFloatingPanel from './EnhancedFloatingPanel';
import { FLOATING_PANEL_BAR_HEIGHT } from './constants';
import PreBookingInfoModal from './PreBookingInfoModal';

// Specifically for mobile view
const bodyCss = css`
  margin-top: 45px;
  overflow: hidden;
`;

const TourEditLayout: React.FC = () => {
  const { md } = useBreakpoint();
  const { setAppHeaderVisibility } = useContext(LayoutContext);
  const { topElementHeights } = useContext(LayoutContext);
  useEffect(() => {
    if (!md) {
      setAppHeaderVisibility(false);
      return () => setAppHeaderVisibility(true);
    }
  }, [md, setAppHeaderVisibility]);

  const {
    mobileSidenavRef,
    isSorting,
    preBookingModalItem,
    prebookingOnOk,
    prebookingOnClose,
  } = useContext(TourEditContext);
  const floatingPanelAnchors = useFloatingPanelAnchors();
  const maxHeight = floatingPanelAnchors[floatingPanelAnchors.length - 1];
  const isMobileView = md === false;

  let navContent = (
    <StyledTourSideNavContainer
      $maxHeight={maxHeight}
      $isMobileView={isMobileView}
    >
      <Page
        title="Edit Tour"
        type="filled"
        fillScreenHeight={!isMobileView}
        {...(isMobileView && { bodyCss })}
        showBackButton
        headerExtra={<TourHeaderMenu />}
        headerVerticalAlign="center"
        toursMobileView={isMobileView}
      >
        <TourSideNav />
      </Page>
    </StyledTourSideNavContainer>
  );

  if (!md) {
    navContent = (
      <EnhancedFloatingPanel
        ref={mobileSidenavRef}
        anchors={
          // if adjusting first anchor point, adjust map height in TourMapView/index as well
          floatingPanelAnchors
        }
        handleDraggingOfContent={!isSorting}
        topMargin={topElementHeights.bbAssume - FLOATING_PANEL_BAR_HEIGHT}
      >
        {navContent}
      </EnhancedFloatingPanel>
    );
  }

  return (
    <>
      <PreBookingInfoModal
        item={preBookingModalItem}
        onOk={prebookingOnOk}
        onClose={prebookingOnClose}
      />
      <StyledTourContainer
        $isMobileView={isMobileView}
        id={'tour-edit-page-root'}
      >
        {navContent}
        <TourMapView />
        {isMobileView && <SendRequests />}
      </StyledTourContainer>
      <TourListingModal />
    </>
  );
};

export default TourEditLayout;

const StyledTourSideNavContainer = styled.div<{
  $isMobileView: boolean;
  $maxHeight: number;
}>`
  width: ${({ $isMobileView }) =>
    $isMobileView ? '100vw' : 'min(447px, 100vw)'};
  box-shadow:
    0px 3px 6px -4px rgba(0, 0, 0, 0.12),
    0px 6px 16px rgba(0, 0, 0, 0.08),
    0px 9px 28px 8px rgba(0, 0, 0, 0.05);
  ${({ $isMobileView, $maxHeight }) =>
    $isMobileView && `height: ${$maxHeight}px;`}
  ${({ $isMobileView }) => $isMobileView && `background-color: #f0f2f5`}
`;

// Use grid multiple column only in web view
// Mobile view needs only one column
const StyledTourContainer = styled.div<{ $isMobileView: boolean }>`
  display: grid;
  column-gap: 0px;
  grid-template-columns: ${({ $isMobileView }) =>
    $isMobileView ? '1fr' : 'min(447px, 100vw) 1fr'};
`;
